import React from 'react';
import { Container, Row, Col, Card, Button, Table, Spinner, Alert, Form, Modal } from 'react-bootstrap';
import { 
  BuildingStorefrontIcon,
  ShoppingCartIcon,
  ShoppingBagIcon,
  TrashIcon,
  CheckIcon,
  CreditCardIcon,
  DocumentTextIcon,
  CalendarIcon
} from '@heroicons/react/24/solid';
import Header from '../common/Header';
import Footer from '../common/Footer';
import { useOrderCart } from './hooks/useOrderCart';

const OrderCart: React.FC = () => {
  const {
    user,
    isExpired,
    navigate,
    cartItems,
    loading,
    asyncState,
    error,
    orderDetails,
    setOrderDetails,
    showSuccessModal,
    setShowSuccessModal,
    deliveryRequired,
    minDeliveryDate,
    leadDays,
    updateQuantity,
    removeItem,
    handlePlaceOrder,
    calculateSubtotal,
    subtotal,
    discountAmount,
    total,
  } = useOrderCart();

  return (
    <div className="d-flex flex-column min-vh-100 app-background">
      <Header />
      <div className="flex-grow-1">
        <Container className="py-3 py-md-5 page-content-padding">
          {/* Page Header */}
          <div className="text-center mb-3 mb-md-5">
            <h1 className="display-4 fw-bold text-primary mb-2 d-flex align-items-center justify-content-center gap-2">
              <ShoppingCartIcon className="page-header-icon" style={{ width: '40px', height: '40px' }} />
              Shopping Cart
            </h1>
            <p className="lead text-muted mb-0">Review your items before placing order</p>
          </div>

        <Row className="g-4">
          <Col lg={8} className="mb-4">
            <Card className="professional-card">
              <Card.Header>
                <h5 className="mb-0 d-flex align-items-center gap-2">
                  <ShoppingBagIcon style={{ width: '20px', height: '20px' }} />
                  Cart Items
                </h5>
              </Card.Header>
              <Card.Body>
                {error && (
                  <Alert variant="danger" className="mb-3">
                    {error}
                  </Alert>
                )}
                
                {loading ? (
                  <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-2 text-muted">Loading cart items...</p>
                  </div>
                ) : Array.isArray(cartItems) && cartItems.length > 0 ? (
                  <div className="table-responsive">
                    <Table className="table-modern d-none d-md-table" hover>
                      <thead>
                        <tr>
                          <th>Product</th>
                          <th>Price</th>
                          <th>Quantity</th>
                          <th>Subtotal</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {cartItems.map((item) => {
                          const price = typeof item.product.price_per_unit === 'string' 
                            ? parseFloat(item.product.price_per_unit) 
                            : item.product.price_per_unit;
                          const itemSubtotal = calculateSubtotal(item);
                          
                          return (
                            <tr key={item.id}>
                              <td>
                                <div className="d-flex align-items-center">
                                  <div className="me-3 d-flex align-items-center">
                                    <BuildingStorefrontIcon style={{ width: '24px', height: '24px' }} className="text-muted" />
                                  </div>
                                  <div>
                                    <div className="fw-semibold">{item.product.name}</div>
                                    <small className="text-muted">{item.product.description}</small>
                                  </div>
                                </div>
                              </td>
                              <td>
                                <span className="fw-semibold text-primary">
                                  ${price.toFixed(2)}
                                </span>
                              </td>
                              <td>
                                <div className="d-flex align-items-center quantity-controls">
                                  <Button 
                                    variant="outline-primary" 
                                    size="sm" 
                                    className="rounded-circle"
                                    style={{ width: '32px', height: '32px' }}
                                    onClick={() => updateQuantity(item.id, item.quantity - 1)}
                                    disabled={asyncState.updating[item.id] || item.quantity <= 1 || isExpired}
                                  >
                                    −
                                  </Button>
                                  <span className="mx-3 fw-bold fs-5">{item.quantity}</span>
                                  <Button 
                                    variant="outline-primary" 
                                    size="sm" 
                                    className="rounded-circle"
                                    style={{ width: '32px', height: '32px' }}
                                    onClick={() => updateQuantity(item.id, item.quantity + 1)}
                                    disabled={asyncState.updating[item.id] || isExpired}
                                  >
                                    +
                                  </Button>
                                </div>
                              </td>
                              <td>
                                <span className="fw-bold text-success fs-5">
                                  ${itemSubtotal.toFixed(2)}
                                </span>
                              </td>
                              <td>
                                <Button 
                                  variant="outline-danger" 
                                  size="sm" 
                                  className="rounded-pill"
                                  onClick={() => removeItem(item.id)}
                                  disabled={asyncState.updating[item.id] || isExpired}
                                >
                                  {asyncState.updating[item.id] ? (
                                    <Spinner size="sm" animation="border" />
                                  ) : (
                                    <>
                                      <TrashIcon style={{ width: '16px', height: '16px' }} className="me-1" />
                                      Remove
                                    </>
                                  )}
                                </Button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>

                    {/* Mobile Card Layout */}
                    <div className="d-md-none">
                      {cartItems.map((item) => {
                        const price = typeof item.product.price_per_unit === 'string' 
                          ? parseFloat(item.product.price_per_unit) 
                          : item.product.price_per_unit;
                        const itemSubtotal = calculateSubtotal(item);
                        
                        return (
                          <Card key={item.id} className="mb-3 border">
                            <Card.Body className="p-3">
                              <div className="d-flex align-items-start justify-content-between mb-2">
                                <div className="flex-grow-1">
                                  <h6 className="mb-1 fw-bold">{item.product.name}</h6>
                                  <small className="text-muted">{item.product.description}</small>
                                </div>
                                <div className="text-end">
                                  <div className="fw-bold text-primary">${price.toFixed(2)}</div>
                                  <small className="text-muted">per {item.product.unit}</small>
                                </div>
                              </div>
                              
                              <div className="d-flex align-items-center justify-content-between mb-2">
                                <div className="quantity-controls d-flex align-items-center">
                                  <Button 
                                    variant="outline-primary" 
                                    size="sm"
                                    disabled={asyncState.updating[item.id] || item.quantity <= 1 || isExpired}
                                    onClick={() => updateQuantity(item.id, item.quantity - 1)}
                                    className="me-2"
                                    style={{ minWidth: '32px', height: '32px' }}
                                  >
                                    -
                                  </Button>
                                  <span className="mx-3 fw-semibold">{item.quantity}</span>
                                  <Button 
                                    variant="outline-primary" 
                                    size="sm"
                                    disabled={asyncState.updating[item.id] || isExpired}
                                    onClick={() => updateQuantity(item.id, item.quantity + 1)}
                                    className="me-2"
                                    style={{ minWidth: '32px', height: '32px' }}
                                  >
                                    +
                                  </Button>
                                </div>
                                
                                <div className="text-end">
                                  <div className="fw-bold">${itemSubtotal.toFixed(2)}</div>
                                  <Button
                                    variant="outline-danger"
                                    size="sm"
                                    disabled={asyncState.updating[item.id] || isExpired}
                                    onClick={() => removeItem(item.id)}
                                    className="mt-1"
                                  >
                                    <TrashIcon style={{ width: '14px', height: '14px' }} />
                                  </Button>
                                </div>
                              </div>
                            </Card.Body>
                          </Card>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <div className="empty-state">
                    <h5 className="text-muted mb-3">Your cart is empty</h5>
                    <p className="text-muted mb-4">Add some delicious bakery items to get started!</p>
                    <Button 
                      variant="primary" 
                      onClick={() => navigate('/products')}
                      className="btn-gradient"
                    >
                      <ShoppingBagIcon style={{ width: '20px', height: '20px' }} className="me-2" />
                      Start Shopping
                    </Button>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>

          <Col lg={4}>
            <div className="sticky-top" style={{ top: '2rem' }}>
              <Card className="professional-card mb-4">
                <Card.Header>
                  <h5 className="mb-0 d-flex align-items-center gap-2">
                    <CreditCardIcon style={{ width: '20px', height: '20px' }} />
                    Order Summary
                  </h5>
                </Card.Header>
                <Card.Body>
                  <div className="summary-line">
                    <span>Subtotal:</span>
                    <span className="fw-semibold">${subtotal.toFixed(2)}</span>
                  </div>
                  {user?.discount_percentage && user.discount_percentage > 0 && (
                    <div className="summary-line">
                      <span>Customer Discount ({user.discount_percentage}%):</span>
                      <span className="fw-semibold text-success">-${discountAmount.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="summary-line">
                    <span>Tax:</span>
                    <span className="fw-semibold">$0.00</span>
                  </div>
                  <div className="summary-line">
                    <span>Delivery:</span>
                    <span className="fw-semibold text-success">FREE</span>
                  </div>
                  <hr className="my-3" />
                  <div className="summary-line total-line">
                    <strong className="fs-5">Total:</strong>
                    <strong className="fs-5 text-primary">${total.toFixed(2)}</strong>
                  </div>
                  <div className="d-grid gap-3 mt-4">
                    <Button 
                      variant="success" 
                      size="lg"
                      className="btn-gradient-success"
                      disabled={!Array.isArray(cartItems) || cartItems.length === 0 || asyncState.placingOrder || isExpired}
                      onClick={handlePlaceOrder}
                    >
                      {asyncState.placingOrder ? (
                        <>
                          <Spinner size="sm" className="me-2" />
                          Placing Order...
                        </>
                      ) : (
                        <>
                          <CheckIcon style={{ width: '20px', height: '20px' }} className="me-2" />
                          Place Order
                        </>
                      )}
                    </Button>
                    <Button 
                      variant="outline-primary" 
                      onClick={() => navigate('/products')}
                      className="btn-outline-gradient"
                    >
                      <ShoppingBagIcon style={{ width: '20px', height: '20px' }} className="me-2" />
                      Continue Shopping
                    </Button>
                  </div>
                </Card.Body>
              </Card>

              <Card className="professional-card">
                <Card.Header>
                  <h6 className="mb-0 d-flex align-items-center gap-2">
                    <CalendarIcon style={{ width: '20px', height: '20px' }} />
                    Delivery Date
                  </h6>
                </Card.Header>
                <Card.Body>
                  <Form.Control
                    type="date"
                    value={orderDetails.deliveryDate}
                    onChange={(e) => setOrderDetails(prev => ({ ...prev, deliveryDate: e.target.value }))}
                    min={minDeliveryDate}
                    className="modern-input"
                    required={deliveryRequired}
                  />
                  <small className="text-muted mt-2 d-block">
                    {deliveryRequired
                      ? `Delivery requires at least ${leadDays} day${leadDays > 1 ? 's' : ''} advance notice.`
                      : 'Select your preferred delivery date.'
                    }
                  </small>
                </Card.Body>
              </Card>

              <Card className="professional-card mt-3">
                <Card.Header>
                  <h6 className="mb-0 d-flex align-items-center gap-2">
                    <DocumentTextIcon style={{ width: '20px', height: '20px' }} />
                    Order Notes
                  </h6>
                </Card.Header>
                <Card.Body>
                  <textarea 
                    className="form-control modern-input" 
                    rows={3} 
                    placeholder="Add any special instructions..."
                    value={orderDetails.notes}
                    onChange={(e) => setOrderDetails(prev => ({ ...prev, notes: e.target.value }))}
                    aria-label="Order notes"
                  ></textarea>
                  <small className="text-muted mt-2 d-block">
                    Let us know about delivery preferences, allergies, or special requests.
                  </small>
                </Card.Body>
              </Card>
            </div>
          </Col>
        </Row>
        </Container>
      </div>
      <Footer />

      {/* Order Success Modal */}
      <Modal show={showSuccessModal} centered size="sm" backdrop="static" keyboard={false}>
        <Modal.Header style={{ backgroundColor: '#f0fdf4', borderBottom: '2px solid #bbf7d0' }}>
          <Modal.Title style={{ color: '#065f46', fontSize: '1.1rem' }} className="d-flex align-items-center gap-2">
            <CheckIcon style={{ width: '22px', height: '22px', color: '#059669' }} />
            Order Placed!
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ fontSize: '0.95rem' }}>
          Your order has been placed successfully. You will be redirected to your orders.
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="success"
            size="sm"
            onClick={() => {
              setShowSuccessModal(false);
              navigate('/customer-dashboard');
            }}
          >
            View My Orders
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default OrderCart;